import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Models } from '../../models/global.models';
import { GetSecurityProfilesResponse, SecurityProfileModel } from '../../loan-state/security-profile';
import { PermissionsModel } from '../../cmg-1003/models';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private readonly _http = inject(HttpClient);
  private _currentUserInfo$: Observable<Models.CurrentUserInfo> | null = null;

  getSecurityProfiles(): Observable<GetSecurityProfilesResponse> {
    const apiUrl = `${environment.retail.apiUrl}/currentuser/securityprofiles`;
    return this._http.get<GetSecurityProfilesResponse>(apiUrl);
  }

  /**
   * Obtain security profiles for current user
   * @return OK with current user security profile
   * @param id userorgprofileid
   */
  getSecurityProfileByUserOrgProfileId(id: number) {
    return this._http.get<SecurityProfileModel>(`${environment.retail.apiUrl}/currentuser/userorgprofileid/${id}/securityprofile`);
  }

  getCurrentUserInfo(): Observable<Models.CurrentUserInfo> {
    if (!this._currentUserInfo$) {
      const apiUrl = `${environment.retail.apiUrl}/currentuser`;
      this._currentUserInfo$ = this._http.get<Models.CurrentUserInfo>(apiUrl).pipe(shareReplay(1));
    }
    return this._currentUserInfo$;
  }

  getUserSettings(): Observable<Models.UserSettings> {
    const apiUrl = `${environment.retail.apiUrl}/users/settings`;
    const userSettings = this._http.get<Models.UserSettingsResponse>(apiUrl).pipe(
      map((r) => {
        if (r.value) {
          const settings = JSON.parse(r.value);
          settings.guid = r.guid;
          return settings;
        }
      }),
    );
    return userSettings;
  }

  updateUserSettings(settings: Models.UserSettings): void {
    const apiUrl = `${environment.retail.apiUrl}/users/settings`;
    this._http.patch<void>(apiUrl, { value: JSON.stringify(settings) }).subscribe();
  }

  /**
   * Converts bitmap feature permissions to JavaScript objects
   * NOTE: Mutates the original object
   * @param profile
   */
  convertFeaturePermissions(profile: SecurityProfileModel) {
    const raw = profile.featurePermissions as Record<string, unknown>;
    Object.keys(raw).forEach((key: string) => {
      const value = raw[key];
      if (typeof value === 'number') {
        raw[key] = { permission: PermissionsModel.FromBitmap(value) };
      }
    });
  }
}
